import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

function ViewTasks() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const operatorId = sessionStorage.getItem('userId') || 1;
      
      console.log('Fetching tasks for operator:', operatorId);
      
      const response = await fetch(`${API_BASE}/tasks_api.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'get_recent_tasks',
          operator_id: parseInt(operatorId)
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Tasks data:', data);
      
      // Debug: Check what edited_status values are received
      console.log('📊 Edited Status values:', data.map(task => ({
        id: task.id,
        video_name: task.video_name,
        edited_status: task.edited_status,
        edited_video_link: task.edited_video_link,
        video_type: task.video_type,
        designer_name: task.designer_name,
        thumbnail_link: task.thumbnail_link,
        designer_notes: task.designer_notes
      })));
      
      let filteredTasks = data;
      if (filter !== 'all') {
        filteredTasks = data.filter(task => task.status === filter);
      }
      
      setTasks(filteredTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Error loading tasks: ' + error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTasks();
  };

  // Enhanced color scheme with gradients
  const colors = {
    primary: '#6366F1',
    secondary: '#8B5CF6',
    accent: '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    dark: '#1E293B',
    light: '#F8FAFC',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
  };

  // Improved editor assignment check
  const isEditorAssigned = (task) => {
    const hasEditorId = task.editor_id != null && task.editor_id !== 0 && task.editor_id !== '0' && task.editor_id !== '';
    const hasEditorName = task.editor_name && task.editor_name !== '' && task.editor_name !== 'Unassigned';
    const hasEditorField = task.editor && task.editor !== '' && task.editor !== 'Unassigned';
    
    return hasEditorId || hasEditorName || hasEditorField;
  };

  // Get editor display name
  const getEditorDisplayName = (task) => {
    if (task.editor_name && task.editor_name !== '' && task.editor_name !== 'Unassigned') {
      return task.editor_name;
    }
    if (task.editor && task.editor !== '' && task.editor !== 'Unassigned') {
      return task.editor;
    }
    return 'Unassigned';
  };

  // Get operator display name
  const getOperatorDisplayName = (task) => {
    if (task.operator_name && task.operator_name !== '' && task.operator_name !== 'Unassigned') {
      return task.operator_name;
    }
    if (task.operator && task.operator !== '' && task.operator !== 'Unassigned') {
      return task.operator;
    }
    return 'Unassigned';
  };

  // Get designer display name
  const getDesignerDisplayName = (task) => {
    if (task.designer_name && task.designer_name !== '' && task.designer_name !== 'Unassigned') {
      return task.designer_name;
    }
    if (task.designer && task.designer !== '' && task.designer !== 'Unassigned') {
      return task.designer;
    }
    return 'Unassigned';
  };

  // Handle raw link click
  const handleRawLinkClick = (link) => {
    if (link && link !== '-' && link !== 'Open' && link !== 'NULL' && link !== '') {
      window.open(link, '_blank');
    }
  };

  // Handle thumbnail link click
  const handleThumbnailLinkClick = (link) => {
    if (link && link !== '-' && link !== 'Open' && link !== 'NULL' && link !== '') {
      window.open(link, '_blank');
    }
  };

  // Navigation handlers
  const handleVideoNameClick = (task) => {
    navigate('/task-details', { 
      state: { 
        task: {
          id: task.id,
          videoName: task.video_name,
          videoType: task.video_type,
          client: task.client_name,
          editor: getEditorDisplayName(task),
          designer: getDesignerDisplayName(task),
          status: task.status,
          rawLink: task.raw_link,
          rawDriveLink: task.raw_drive_link,
          editedLink: task.edited_video_link,
          thumbnailLink: task.thumbnail_link,
          shootDate: task.shoot_date,
          videoCount: task.video_count,
          count: task.count,
          operatorName: task.operator_name,
          assignedByName: task.assigned_by_name,
          referenceVideoLink: task.reference_video_link,
          notes: task.notes,
          editorNotes: task.editor_notes,
          designerNotes: task.designer_notes,
          editedStatus: task.edited_status
        }
      } 
    });
  };

  const handleClientClick = (clientId, clientName) => {
    navigate('/client-tasks', { 
      state: { 
        clientId: clientId,
        clientName: clientName,
        tasks: tasks.filter(task => task.client_id === clientId)
      } 
    });
  };

  // Handle editor click with task filtering
  const handleEditorClick = (editorName) => {
    console.log('🎯 Navigating to editor tasks for:', editorName);
    
    const editorTasks = tasks.filter(task => {
      const taskEditorName = getEditorDisplayName(task);
      return taskEditorName === editorName && taskEditorName !== 'Unassigned';
    });
    
    console.log(`📊 Found ${editorTasks.length} tasks for editor ${editorName}`);
    
    if (editorTasks.length > 0) {
      navigate('/editor-tasks', { 
        state: { 
          editorName: editorName,
          tasks: editorTasks
        } 
      });
    } else {
      setError(`No tasks found for editor ${editorName}`);
    }
  };

  // Handle operator click with task filtering
  const handleOperatorClick = (operatorName) => {
    console.log('🎯 Navigating to operator tasks for:', operatorName);
    
    const operatorTasks = tasks.filter(task => {
      const taskOperatorName = getOperatorDisplayName(task);
      return taskOperatorName === operatorName && taskOperatorName !== 'Unassigned';
    });
    
    console.log(`📊 Found ${operatorTasks.length} tasks for operator ${operatorName}`);
    
    if (operatorTasks.length > 0) {
      navigate('/operator-tasks', { 
        state: { 
          operatorName: operatorName,
          tasks: operatorTasks
        } 
      });
    } else {
      setError(`No tasks found for operator ${operatorName}`);
    }
  };

  // Handle designer click with task filtering
  const handleDesignerClick = (designerName) => {
    console.log('🎯 Navigating to designer tasks for:', designerName);
    
    const designerTasks = tasks.filter(task => {
      const taskDesignerName = getDesignerDisplayName(task);
      return taskDesignerName === designerName && taskDesignerName !== 'Unassigned';
    });
    
    console.log(`📊 Found ${designerTasks.length} tasks for designer ${designerName}`);
    
    if (designerTasks.length > 0) {
      navigate('/designer-tasks', { 
        state: { 
          designerName: designerName,
          tasks: designerTasks
        } 
      });
    } else {
      setError(`No tasks found for designer ${designerName}`);
    }
  };

  const handleEditedLinkClick = (link) => {
    if (link && link !== '-' && link !== 'Open' && link !== 'NULL') {
      window.open(link, '_blank');
    }
  };

  // FIXED: Get Edited Status based on edited_status field from database
  const getEditedStatus = (task) => {
    const editedStatus = task.edited_status || 'pending';
    
    const statusConfig = {
      pending: { status: 'pending', text: 'Not Started', class: 'status-pending', icon: 'fa-clock' },
      in_progress: { status: 'in_progress', text: 'Editing', class: 'status-in-progress', icon: 'fa-edit' },
      review: { status: 'review', text: 'In Review', class: 'status-review', icon: 'fa-eye' },
      completed: { status: 'completed', text: 'Completed', class: 'status-completed', icon: 'fa-check' },
      revisions: { status: 'revisions', text: 'Revisions', class: 'status-revisions', icon: 'fa-sync' },
      submitted: { status: 'submitted', text: 'Submitted', class: 'status-submitted', icon: 'fa-paper-plane' }
    };
    
    return statusConfig[editedStatus] || statusConfig.pending;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      unassigned: { class: 'status-unassigned', text: 'Unassigned', icon: 'fa-clock' },
      assigned: { class: 'status-assigned', text: 'Assigned', icon: 'fa-user' },
      in_progress: { class: 'status-in-progress', text: 'In Progress', icon: 'fa-spinner' },
      in_review: { class: 'status-review', text: 'In Review', icon: 'fa-eye' },
      needs_revision: { class: 'status-revisions', text: 'Needs Revision', icon: 'fa-exclamation' },
      completed: { class: 'status-completed', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'status-completed', text: 'Approved', icon: 'fa-check-double' },
      cancelled: { class: 'status-cancelled', text: 'Cancelled', icon: 'fa-times' },
      pending: { class: 'status-pending', text: 'Pending', icon: 'fa-clock' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return config;
  };

  // Format video type for display
  const formatVideoType = (videoType) => {
    if (!videoType || videoType === '' || videoType === 'NULL') return 'N/A';
    
    const typeMap = {
      'indoor_reel': 'Indoor Reel',
      'outdoor_reel': 'Outdoor Reel',
      'podcast': 'Podcast',
      'indoor_long_video': 'Indoor Long Video',
      'outdoor_long_video': 'Outdoor Long Video'
    };
    
    return typeMap[videoType] || videoType;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'NULL') return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Truncate long text for display
  const truncateText = (text, maxLength = 50) => {
    if (!text || text === '-' || text === 'NULL') return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Format video count display
  const formatVideoCount = (count) => {
    if (!count || count === 0 || count === '0') return '0';
    return count.toString();
  };

  // Check if designer is assigned
  const isDesignerAssigned = (task) => {
    const designerName = getDesignerDisplayName(task);
    return designerName && designerName !== 'Unassigned' && designerName !== '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="view-tasks-container" style={styles.container}>
          <div className="loading-state" style={styles.loadingState}>
            <div className="loading-animation" style={styles.loadingAnimation}>
              <div className="loading-dots" style={styles.loadingDots}>
                <div className="dot" style={styles.dot}></div>
                <div className="dot" style={styles.dot}></div>
                <div className="dot" style={styles.dot}></div>
              </div>
            </div>
            <h3 style={styles.loadingText}>Loading Your Tasks</h3>
            <p style={styles.loadingSubtext}>Preparing your task overview</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="view-tasks-container" style={styles.container}>
        {/* Animated Background */}
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        {/* Header Section */}
        <div className="tasks-header" style={styles.tasksHeader}>
          <div className="header-content" style={styles.headerContent}>
            <div className="welcome-section">
              <div className="greeting" style={styles.greeting}>
                <h1 className="tasks-title" style={styles.tasksTitle}>
                  <i className="fas fa-tasks me-2"></i>
                  View Tasks
                </h1>
                <div className="welcome-badge" style={styles.welcomeBadge}>
                  <span className="badge-text" style={styles.badgeText}>Task Management</span>
                  <span className="badge-role" style={styles.badgeRole}>Operator</span>
                </div>
              </div>
              <p className="welcome-message" style={styles.welcomeMessage}>
                Manage and track all your video editing tasks
              </p>
            </div>
            <div className="header-actions" style={styles.headerActions}>
              <button 
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={refreshing}
                style={styles.refreshBtn}
              >
                <div className="refresh-icon" style={styles.refreshIcon}>
                  <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                </div>
                <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-alert" style={styles.errorAlert}>
            <div className="alert-icon" style={styles.alertIcon}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="alert-message" style={styles.alertMessage}>
              <strong>Attention Needed</strong>
              <span style={{color: 'white'}}>{error}</span>
            </div>
            <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="tasks-content" style={styles.tasksContent}>
          {/* Filter Section */}
          <div className="filter-section" style={styles.filterSection}>
            <div className="section-header" style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Filter Tasks</h3>
              <p style={styles.sectionSubtitle}>View tasks by status</p>
            </div>
            <div className="filter-buttons" style={styles.filterButtons}>
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
                style={filter === 'all' ? styles.filterBtnActive : styles.filterBtn}
              >
                <i className="fas fa-layer-group me-2"></i>
                All Tasks
              </button>
              <button
                className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                onClick={() => setFilter('pending')}
                style={filter === 'pending' ? {...styles.filterBtnActive, ...styles.filterBtnWarning} : {...styles.filterBtn, ...styles.filterBtnWarning}}
              >
                <i className="fas fa-clock me-2"></i>
                Pending
              </button>
              <button
                className={`filter-btn ${filter === 'in_progress' ? 'active' : ''}`}
                onClick={() => setFilter('in_progress')}
                style={filter === 'in_progress' ? {...styles.filterBtnActive, ...styles.filterBtnInfo} : {...styles.filterBtn, ...styles.filterBtnInfo}}
              >
                <i className="fas fa-spinner me-2"></i>
                In Progress
              </button>
              <button
                className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
                onClick={() => setFilter('completed')}
                style={filter === 'completed' ? {...styles.filterBtnActive, ...styles.filterBtnSuccess} : {...styles.filterBtn, ...styles.filterBtnSuccess}}
              >
                <i className="fas fa-check me-2"></i>
                Completed
              </button>
            </div>
            <div className="filter-info" style={styles.filterInfo}>
              <small style={{color: 'rgba(255, 255, 255, 0.7)'}}>
                Showing {tasks.length} task{tasks.length !== 1 ? 's' : ''} 
                {filter !== 'all' && ` (filtered by ${filter.replace('_', ' ')})`}
              </small>
            </div>
          </div>

          {/* Tasks Table */}
          <div className="tasks-table-section" style={styles.tasksTableSection}>
            <div className="table-card" style={styles.tableCard}>
              <div className="table-card-header" style={styles.tableCardHeader}>
                <h3 style={styles.tableCardTitle}>
                  <i className="fas fa-list me-2"></i>
                  Tasks List
                </h3>
              </div>
              <div className="table-card-body" style={styles.tableCardBody}>
                {tasks.length === 0 ? (
                  <div className="empty-state" style={styles.emptyState}>
                    <i className="fas fa-tasks" style={styles.emptyStateIcon}></i>
                    <h4 style={styles.emptyStateTitle}>No tasks found</h4>
                    <p style={styles.emptyStateText}>
                      {filter === 'all' 
                        ? "No tasks found in the system." 
                        : `No ${filter.replace('_', ' ')} tasks found.`
                      }
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive" style={styles.tableResponsive}>
                    <table className="tasks-table" style={styles.tasksTable}>
                      <thead>
                        <tr>
                          {[
                            '#', 'Video Name', 'Video Type', 'Client', 'Editor', 'Designer', 'Operator', 'Status', 
                            'Raw Link', 'Thumbnail Link', 'Video Count', 'Edited Status', 'Notes', 
                            'Editor Notes', 'Designer Notes', 'Shoot Date', 'Edited Link', 'Update'
                          ].map((header, index) => (
                            <th key={index} style={styles.tableHeader}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, index) => {
                          const editedStatus = getEditedStatus(task);
                          const statusConfig = getStatusBadge(task.status);
                          const isAssigned = isEditorAssigned(task);
                          const isDesigner = isDesignerAssigned(task);
                          const editorName = getEditorDisplayName(task);
                          const operatorName = getOperatorDisplayName(task);
                          const designerName = getDesignerDisplayName(task);
                          
                          return (
                            <tr key={task.id} style={styles.tableRow}>
                              <td style={styles.tableCell}>
                                <span style={styles.taskNumber}>{index + 1}</span>
                              </td>
                              
                              {/* Video Name - WHITE COLOR */}
                              <td style={styles.tableCell}>
                                <button 
                                  className="video-name-btn"
                                  onClick={() => handleVideoNameClick(task)}
                                  style={styles.videoNameBtn}
                                >
                                  {task.video_name || 'N/A'}
                                </button>
                              </td>

                              {/* Video Type - WHITE COLOR */}
                              <td style={styles.tableCell}>
                                <span style={styles.whiteText}>
                                  {formatVideoType(task.video_type)}
                                </span>
                              </td>
                              
                              {/* Client - WHITE COLOR */}
                              <td style={styles.tableCell}>
                                <button 
                                  className="client-btn"
                                  onClick={() => handleClientClick(task.client_id, task.client_name)}
                                  style={styles.clickableBtn}
                                >
                                  {task.client_name || 'Unknown Client'}
                                </button>
                              </td>
                              
                              {/* Editor - WHITE COLOR */}
                              <td style={styles.tableCell}>
                                {isAssigned ? (
                                  <button 
                                    className="editor-btn"
                                    onClick={() => handleEditorClick(editorName)}
                                    style={styles.clickableBtn}
                                  >
                                    {editorName}
                                  </button>
                                ) : (
                                  <span style={styles.unassignedText}>Unassigned</span>
                                )}
                              </td>

                              {/* Designer - WHITE COLOR */}
                              <td style={styles.tableCell}>
                                {isDesigner ? (
                                  <button 
                                    className="designer-btn"
                                    onClick={() => handleDesignerClick(designerName)}
                                    style={styles.clickableBtn}
                                  >
                                    {designerName}
                                  </button>
                                ) : (
                                  <span style={styles.unassignedText}>Unassigned</span>
                                )}
                              </td>
                              
                              {/* Operator - WHITE COLOR */}
                              <td style={styles.tableCell}>
                                <button 
                                  className="operator-btn"
                                  onClick={() => handleOperatorClick(operatorName)}
                                  style={styles.clickableBtn}
                                >
                                  {operatorName}
                                </button>
                              </td>
                              
                              {/* Status (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span className={`status-badge ${statusConfig.class}`} style={getStatusBadgeStyle(statusConfig.class)}>
                                  <i className={`fas ${statusConfig.icon} me-1`}></i>
                                  {statusConfig.text}
                                </span>
                              </td>
                              
                              {/* Raw Link (keep original colors) */}
                              <td style={styles.tableCell}>
                                <button 
                                  className="raw-link-btn"
                                  onClick={() => handleRawLinkClick(task.raw_link)}
                                  disabled={!task.raw_link || task.raw_link === '-' || task.raw_link === 'Open' || task.raw_link === 'NULL' || task.raw_link === ''}
                                  style={getLinkButtonStyle(task.raw_link, 'raw')}
                                >
                                  <i className="fas fa-external-link-alt me-1"></i>
                                  Raw
                                </button>
                              </td>

                              {/* Thumbnail Link (keep original colors) */}
                              <td style={styles.tableCell}>
                                <button 
                                  className="thumbnail-link-btn"
                                  onClick={() => handleThumbnailLinkClick(task.thumbnail_link)}
                                  disabled={!task.thumbnail_link || task.thumbnail_link === '-' || task.thumbnail_link === 'Open' || task.thumbnail_link === 'NULL' || task.thumbnail_link === ''}
                                  style={getLinkButtonStyle(task.thumbnail_link, 'thumbnail')}
                                >
                                  <i className="fas fa-image me-1"></i>
                                  Thumb
                                </button>
                              </td>
                              
                              {/* Video Count (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span className="video-count-badge" style={styles.videoCountBadge}>
                                  <i className="fas fa-video me-1"></i>
                                  {formatVideoCount(task.video_count || task.count)}
                                </span>
                              </td>
                              
                              {/* Edited Status (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span className={`edited-status-badge ${editedStatus.class}`} style={getStatusBadgeStyle(editedStatus.class)}>
                                  <i className={`fas ${editedStatus.icon} me-1`}></i>
                                  {editedStatus.text}
                                </span>
                              </td>
                              
                              {/* Notes (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span style={styles.notesText} title={task.notes || 'No notes'}>
                                  {truncateText(task.notes, 20)}
                                </span>
                              </td>
                              
                              {/* Editor Notes (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span style={styles.notesText} title={task.editor_notes || 'No editor notes'}>
                                  {truncateText(task.editor_notes, 20)}
                                </span>
                              </td>

                              {/* Designer Notes (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span style={styles.notesText} title={task.designer_notes || 'No designer notes'}>
                                  {truncateText(task.designer_notes, 20)}
                                </span>
                              </td>
                              
                              {/* Shoot Date (keep original colors) */}
                              <td style={styles.tableCell}>
                                <span style={styles.dateText}>{formatDate(task.shoot_date)}</span>
                              </td>
                              
                              {/* Edited Link (keep original colors) */}
                              <td style={styles.tableCell}>
                                <button 
                                  className="edited-link-btn"
                                  onClick={() => handleEditedLinkClick(task.edited_video_link)}
                                  disabled={!task.edited_video_link || task.edited_video_link === '-' || task.edited_video_link === 'Open' || task.edited_video_link === 'NULL'}
                                  style={getLinkButtonStyle(task.edited_video_link, 'edited')}
                                >
                                  <i className="fas fa-external-link-alt me-1"></i>
                                  View
                                </button>
                              </td>
                              
                              {/* Update Button (keep original colors) */}
                              <td style={styles.tableCell}>
                                <Link 
                                  to={`/update-task/${task.id}`}
                                  className="update-btn"
                                  style={styles.updateBtn}
                                  state={{ 
                                    taskData: {
                                      id: task.id,
                                      video_name: task.video_name,
                                      video_type: task.video_type,
                                      client_id: task.client_id,
                                      raw_drive_link: task.raw_drive_link,
                                      raw_link: task.raw_link,
                                      edited_video_link: task.edited_video_link,
                                      thumbnail_link: task.thumbnail_link,
                                      reference_video_link: task.reference_video_link,
                                      shoot_date: task.shoot_date,
                                      deadline_date: task.deadline_date,
                                      video_count: task.video_count,
                                      count: task.count,
                                      notes: task.notes,
                                      editor_notes: task.editor_notes,
                                      designer_notes: task.designer_notes,
                                      status: task.status,
                                      operator_id: task.operator_id,
                                      editor_id: task.editor_id,
                                      designer_id: task.designer_id,
                                      client_name: task.client_name,
                                      operator_name: task.operator_name,
                                      editor_name: task.editor_name,
                                      designer_name: task.designer_name
                                    }
                                  }}
                                >
                                  <i className="fas fa-edit me-1"></i>
                                  Update
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          } 40% {
            transform: scale(1);
          }
        }

        @keyframes float {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
      `}</style>
    </Layout>
  );
}

// Helper function to get status badge styles
const getStatusBadgeStyle = (statusClass) => {
  const baseStyle = {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap'
  };

  const statusStyles = {
    'status-pending': { ...baseStyle, background: '#6B7280', color: 'white' },
    'status-in-progress': { ...baseStyle, background: '#06B6D4', color: 'white' },
    'status-review': { ...baseStyle, background: '#F59E0B', color: 'black' },
    'status-completed': { ...baseStyle, background: '#10B981', color: 'white' },
    'status-revisions': { ...baseStyle, background: '#F59E0B', color: 'black' },
    'status-submitted': { ...baseStyle, background: '#6366F1', color: 'white' },
    'status-unassigned': { ...baseStyle, background: '#6B7280', color: 'white' },
    'status-assigned': { ...baseStyle, background: '#6366F1', color: 'white' },
    'status-cancelled': { ...baseStyle, background: '#EF4444', color: 'white' }
  };

  return statusStyles[statusClass] || baseStyle;
};

// Helper function to get link button styles
const getLinkButtonStyle = (link, type) => {
  const baseStyle = {
    padding: '4px 12px',
    borderRadius: '6px',
    border: '1px solid',
    fontSize: '0.75rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.3s ease'
  };

  const isActive = link && link !== '-' && link !== 'Open' && link !== 'NULL' && link !== '';

  if (!isActive) {
    return {
      ...baseStyle,
      background: 'transparent',
      borderColor: '#6B7280',
      color: '#6B7280',
      cursor: 'not-allowed',
      opacity: 0.6
    };
  }

  const typeStyles = {
    'raw': { background: 'transparent', borderColor: '#6366F1', color: '#6366F1' },
    'thumbnail': { background: 'transparent', borderColor: '#F59E0B', color: '#F59E0B' },
    'edited': { background: 'transparent', borderColor: '#10B981', color: '#10B981' }
  };

  return {
    ...baseStyle,
    ...typeStyles[type]
  };
};

// Styles object
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    position: 'relative',
    overflowX: 'hidden'
  },
  animatedBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none'
  },
  floatingOrb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(40px)',
    opacity: 0.3,
    animation: 'float 8s ease-in-out infinite'
  },
  orb1: {
    width: '300px',
    height: '300px',
    background: '#6366F1',
    top: '10%',
    left: '5%',
    animationDelay: '0s'
  },
  orb2: {
    width: '200px',
    height: '200px',
    background: '#8B5CF6',
    top: '60%',
    right: '10%',
    animationDelay: '2s'
  },
  orb3: {
    width: '250px',
    height: '250px',
    background: '#06B6D4',
    bottom: '20%',
    left: '15%',
    animationDelay: '4s'
  },
  orb4: {
    width: '180px',
    height: '180px',
    background: '#10B981',
    top: '30%',
    right: '20%',
    animationDelay: '6s'
  },
  gridOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
    backgroundSize: '50px 50px'
  },
  tasksHeader: {
    padding: '40px 40px 30px',
    position: 'relative',
    zIndex: 2
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  greeting: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '15px'
  },
  tasksTitle: {
    fontSize: '3rem',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: 0
  },
  welcomeBadge: {
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  badgeText: {
    color: 'white',
    fontWeight: '600',
    fontSize: '0.9rem'
  },
  badgeRole: {
    background: '#6366F1',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  welcomeMessage: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '1.2rem',
    margin: 0,
    maxWidth: '600px'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
  refreshBtn: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '12px 20px',
    borderRadius: '15px',
    cursor: 'pointer',
    fontWeight: '600',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  refreshIcon: {
    fontSize: '1.1rem'
  },
  errorAlert: {
    background: 'rgba(239, 68, 68, 0.9)',
    color: 'white',
    padding: '20px',
    borderRadius: '15px',
    margin: '0 40px 30px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  alertIcon: {
    fontSize: '1.5rem'
  },
  alertMessage: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  alertClose: {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    transition: 'background 0.3s ease'
  },
  tasksContent: {
    padding: '0 40px 40px',
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: 2
  },
  filterSection: {
    marginBottom: '40px'
  },
  sectionHeader: {
    marginBottom: '25px'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'white',
    margin: '0 0 8px 0'
  },
  sectionSubtitle: {
    color: 'rgba(255, 255, 255, 0.7)',
    margin: 0,
    fontSize: '1rem'
  },
  filterButtons: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap'
  },
  filterBtn: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '12px 20px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center'
  },
  filterBtnActive: {
    background: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    transform: 'translateY(-2px)'
  },
  filterBtnWarning: {
    borderColor: '#F59E0B',
    color: '#F59E0B'
  },
  filterBtnInfo: {
    borderColor: '#06B6D4',
    color: '#06B6D4'
  },
  filterBtnSuccess: {
    borderColor: '#10B981',
    color: '#10B981'
  },
  filterInfo: {
    marginTop: '15px',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  tasksTableSection: {
    marginBottom: '40px'
  },
  tableCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '25px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    overflow: 'hidden'
  },
  tableCardHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '25px 30px'
  },
  tableCardTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    margin: 0
  },
  tableCardBody: {
    padding: '0'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 40px',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  emptyStateIcon: {
    fontSize: '4rem',
    marginBottom: '20px',
    opacity: 0.5
  },
  emptyStateTitle: {
    color: 'white',
    marginBottom: '10px'
  },
  emptyStateText: {
    margin: 0
  },
  tableResponsive: {
    overflowX: 'auto'
  },
  tasksTable: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    padding: '15px 12px',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '0.85rem',
    borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
    whiteSpace: 'nowrap'
  },
  tableRow: {
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'background 0.3s ease'
  },
  tableCell: {
    padding: '12px',
    color: 'white',
    fontSize: '0.85rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
  },
  whiteText: {
    color: 'white',
    fontWeight: '500'
  },
  taskNumber: {
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.8)'
  },
  videoNameBtn: {
    background: 'none',
    border: 'none',
    color: 'white',  // WHITE COLOR
    fontWeight: 'bold',
    cursor: 'pointer',
    textDecoration: 'none',
    padding: 0,
    textAlign: 'left'
  },
  clickableBtn: {
    background: 'none',
    border: 'none',
    color: 'white',  // WHITE COLOR
    fontWeight: 'bold',
    cursor: 'pointer',
    textDecoration: 'none',
    padding: 0
  },
  unassignedText: {
    color: 'white',  // WHITE COLOR
    fontWeight: '500'
  },
  videoCountBadge: {
    background: '#06B6D4',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center'
  },
  notesText: {
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'help',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  dateText: {
    color: 'rgba(255, 255, 255, 0.8)',
    whiteSpace: 'nowrap'
  },
  updateBtn: {
    background: '#6366F1',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.3s ease'
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    color: 'white',
    textAlign: 'center'
  },
  loadingAnimation: {
    marginBottom: '30px'
  },
  loadingDots: {
    display: 'flex',
    gap: '8px'
  },
  dot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: 'white',
    animation: 'bounce 1.4s ease-in-out infinite both'
  },
  loadingText: {
    fontSize: '1.8rem',
    margin: '0 0 12px 0',
    fontWeight: '600'
  },
  loadingSubtext: {
    color: 'rgba(255, 255, 255, 0.8)',
    margin: 0,
    fontSize: '1.1rem'
  }
};

export default ViewTasks;